import AllAchievements, {
	achievementSchema,
} from "@/comps/feature/achievements"
import Benefits, {
	benefitsSchema,
} from "@/comps/feature/benefits"
import CallToAction, {
	callToActionSchema,
} from "@/comps/feature/call-to-action"
import FAQ, { questionComponent } from "@/comps/feature/faq"
import HeadlineCover, {
	headlineSchema,
} from "@/comps/feature/headline"
import {
	HeadlineAlternative,
	headlineAlternativeSchema,
} from "@/comps/feature/headline-alternative"
import HowToMake, {
	tutorialSchema,
} from "@/comps/feature/how-to-make"
import InfoButton, {
	infoButtonSchema,
} from "@/comps/feature/info-button"
import InfoLeftRight, {
	infoLeftRightSchema,
} from "@/comps/feature/info-left-right"
import IntroLanding, {
	introSchema,
} from "@/comps/feature/intro"
import ReviewsLanding, {
	reviewComponent,
} from "@/comps/feature/reviews"
import Styles, { styleSchema } from "@/comps/feature/styles"
import {
	StylesSection,
	suggestedStyleSchema,
} from "@/comps/feature/suggested-styles"
import SuggestedTemplates, {
	SuggestedTemplatesAlternative,
	Templates,
} from "@/comps/feature/suggested-templates"
import { Text, textSchema } from "@/comps/feature/text"
import HeadlineTextArea, {
	textareaSchema,
} from "@/comps/feature/textarea"
import Tools, { toolComponent } from "@/comps/feature/tools"
import {
	Usecases,
	usecasesSchema,
} from "@/comps/feature/usecases"
import {
	UsecasesAlternative,
	usecasesAlternativeSchema,
} from "@/comps/feature/usecases-alternative"
import { WrappedFooter } from "@/comps/footer"
import Runner from "@/comps/home/platforms/runner"
import {
	FeaturePageProps,
	getLandingPageData,
} from "@/ssr/features"
import { getErrorLog } from "@/utils/errors"
import { SSRNotFound, wrapSSRAuth } from "@/utils/ssr-auth"
import { createContext, useContext } from "react"
import { FAQPage, Thing, WithContext } from "schema-dts"
import { z } from "zod"
import { Header, Meta } from "../../comps"

const featureAiToolSchema = z.enum([
	"deform",
	"ai_photo",
	"ai_restyle",
	"text_to_image",
	"text_to_video",
	"ai_music_generator",
])

export type FeatureTools = z.infer<
	typeof featureAiToolSchema
>

export const templateComponentAlternative = z.object({
	component: z.literal("templates-alternative"),
	title: z.string(),
	description: z.string(),
	button_text: z.string(),
	button_link: z.string(),
	bubble_text: z.string(),
	templates: z.array(z.string()),
})

export const templateComponent = z.object({
	component: z.literal("templates"),
	title: z.string(),
	button_link: z.string().optional(),
	button_text: z.string().optional(),
	templates: z.array(z.string()),
	upload: z.boolean().optional(),
	tool: featureAiToolSchema.optional(),
})

interface PageContextResult {
	page: string
	templates: Templates
}

const PageContext = createContext<PageContextResult | null>(
	null,
)

export function useFeaturePage(): PageContextResult {
	const context = useContext(PageContext)
	if (!context) throw new Error("You forgot the context")

	return context
}

const runnerSchema = z.object({
	component: z.literal("runner"),
})

const metaSchema = z.object({
	component: z.literal("meta"),
	title: z.string(),
	description: z.string(),
	cover: z.string().optional(),
	ratingValue: z.number().optional(),
	ratingCount: z.number().optional(),
	reviewCount: z.number().optional(),
})

const singleFeatureSchema = z.union([
	metaSchema,
	introSchema,
	infoLeftRightSchema,
	headlineSchema,
	runnerSchema,
	achievementSchema,
	tutorialSchema,
	templateComponent,
	styleSchema,
	suggestedStyleSchema,
	usecasesSchema,
	benefitsSchema,
	reviewComponent,
	questionComponent,
	toolComponent,
	templateComponentAlternative,
	usecasesAlternativeSchema,
	headlineAlternativeSchema,
	infoButtonSchema,
	textSchema,
	callToActionSchema,
	textareaSchema,
])

export const featureSchema = z.array(singleFeatureSchema)
export type Feature = z.infer<typeof featureSchema>
type SingleFeature = z.infer<typeof singleFeatureSchema>

interface RenderProps {
	item: SingleFeature
	jsonLd: WithContext<Thing>[]
}

function isFaq(
	component: SingleFeature,
): component is z.infer<typeof questionComponent> {
	return component.component === "faq"
}

function isHeadline(
	component: SingleFeature,
): component is z.infer<typeof headlineSchema> {
	return component.component === "headline"
}

function isMeta(
	component: SingleFeature,
): component is z.infer<typeof metaSchema> {
	return component.component === "meta"
}

function isReview(
	component: SingleFeature,
): component is z.infer<typeof reviewComponent> {
	return component.component === "reviews"
}

function getJsonLdData(
	components: Feature,
	slug: string,
): WithContext<Thing>[] {
	const faqComponent =
		components.filter(isFaq).at(0) ?? null

	const headlineComponent =
		components.filter(isHeadline).at(0) ?? null

	const metaComponent =
		components.filter(isMeta).at(0) ?? null

	const reviewComponent =
		components.filter(isReview).at(0) ?? null

	const questions = faqComponent?.questions ?? null

	const headline = headlineComponent?.title ?? null

	const description = metaComponent?.description ?? null

	const person = reviewComponent?.reviews[0] ?? null

	const scripts: WithContext<Thing>[] = [
		{
			"@context": "https://schema.org",
			"@type": "BreadcrumbList",
			itemListElement: [
				{
					"@type": "ListItem",
					position: "0",
					name: "Home",
					item: "https://zoomerang.app/",
				},
				{
					"@type": "ListItem",
					position: "1",
					name: "Tools",
					item: "https://zoomerang.app/tools",
				},
				{
					"@type": "ListItem",
					position: "2",
					name: headline ?? slug,
					item: "https://zoomerang.app/tools/" + slug,
				},
			],
		},
		{
			"@context": "https://schema.org",
			"@type": "SoftwareApplication",
			name: headline ?? slug,
			aggregateRating: {
				"@type": "AggregateRating",
				ratingValue: metaComponent?.ratingValue ?? 5,
				ratingCount: metaComponent?.ratingCount ?? 21,
				reviewCount: metaComponent?.reviewCount ?? 17,
				bestRating: "5",
				worstRating: "0",
			},
			applicationCategory: "MultimediaApplication",
			offers: {
				"@type": "Offer",
				price: "0",
				priceCurrency: "USD",
			},
			...(person !== null && {
				review: {
					"@type": "Review",
					author: {
						"@type": "Person",
						name: person.name,
					},
					reviewRating: {
						"@type": "Rating",
						ratingValue: "5",
						worstRating: "0",
						bestRating: "5",
					},
					description: person.opinion,
				},
			}),
			operatingSystem:
				"Windows, Mac OS, Linux, Chrome OS, iOS, Android",
			description: description ?? slug,
			url: "https://zoomerang.app/tools/" + slug,
			applicationSubCategory: headline ?? slug,
		},
	]

	if (questions) {
		const questionScript: WithContext<FAQPage> = {
			"@context": "https://schema.org",
			"@type": "FAQPage",
			mainEntity: questions.map((a) => {
				return {
					"@type": "Question",
					name: a.question,
					acceptedAnswer: {
						"@type": "Answer",
						text: a.answer,
					},
				}
			}),
		}
		scripts.push(questionScript)
	}

	return scripts
}

const RenderComponent = (props: RenderProps) => {
	const { item, jsonLd } = props
	switch (item.component) {
		case "meta":
			return (
				<Meta
					title={item.title}
					description={item.description}
					cover={item.cover}
					jsonLdData={jsonLd}
				/>
			)
		case "intro":
			return <IntroLanding {...item} />
		case "headline":
			return <HeadlineCover {...item} />
		case "runner":
			return (
				<Runner
					{...item}
					className="py-[16px] tablet:pt-[40px] desktop:pt-[55px]"
				/>
			)
		case "achievements":
			return <AllAchievements {...item} />
		case "how_to_make":
			return <HowToMake {...item} />
		case "templates":
			return <SuggestedTemplates {...item} />
		case "suggested-styles":
			return <StylesSection {...item} />
		case "usecases":
			return <Usecases {...item} />
		case "templates-alternative":
			return <SuggestedTemplatesAlternative {...item} />
		case "styles":
			return <Styles {...item} />
		case "benefits":
			return <Benefits {...item} />
		case "reviews":
			return <ReviewsLanding {...item} />
		case "faq":
			return <FAQ {...item} />
		case "tools":
			return <Tools {...item} />
		case "usecases_alternative":
			return <UsecasesAlternative {...item} />
		case "headline_alternative":
			return <HeadlineAlternative {...item} />
		case "info-left-right":
			return <InfoLeftRight {...item} />
		case "info-button":
			return <InfoButton {...item} />
		case "text":
			return <Text {...item} />
		case "call-to-action":
			return <CallToAction {...item} />
		case "headline-textarea":
			return <HeadlineTextArea {...item} />
	}
}
export default function Feature(props: FeaturePageProps) {
	const { content, page, templates } = props
	const parsedJson = JSON.parse(content)
	const parsed = featureSchema.parse(parsedJson)

	return (
		<PageContext.Provider value={{ page, templates }}>
			<div className="flex w-full flex-col bg-color-cell">
				<Header />
				<div className="flex w-full flex-col bg-color-cell">
					{parsed.map((componentData, index) => {
						return (
							<RenderComponent
								item={componentData}
								jsonLd={getJsonLdData(parsed, page)}
								key={index}
							/>
						)
					})}
				</div>
				<WrappedFooter />
			</div>
		</PageContext.Provider>
	)
}

export const getServerSideProps =
	wrapSSRAuth<FeaturePageProps>(async function (context) {
		const feature = String(context.params?.feature)

		try {
			const props = await getLandingPageData(
				feature,
				null,
				"feature",
			)

			return props
		} catch (e) {
			getErrorLog(e, "/feature/[slug]")
			throw new SSRNotFound()
		}
	}, [])
